import React, { Component } from "react";
import { loading, alert, NavbarAdmin, Modal, GET, POST, PUT, t } from "../../components/CustomComponent.js";
import { fmtDateTime } from "../../utils/timeTracking.js";
import "../../assets/css/warranty-expiry.css";
import { formatNumber } from "../../utils/formatting";

const THRESHOLDS = [90, 60, 30, 7];
// ช่วงแจ้งเตือนของแผน PM แยกจากชุด 90/60/30/7 (ตายตัว ห้ามเพิ่มวันเอง) · "เกินกำหนด" ส่งทุกรอบ ปิดไม่ได้
const PM_THRESHOLDS = [30, 14, 7, 1];
const KIND_KEYS = ["warranty", "filter", "pm"];
const WEEK_DAYS = [1, 2, 3, 4, 5];
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = ["00", "15", "30", "45"];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const MAX_SUGGEST = 8;
const STATUS_COLORS = { on: "#1F8A4C", off: "#6B7280", no_recipient: "#D64545" };

const fill = (text, values) => Object.keys(values).reduce((acc, k) => acc.replace(`{${k}}`, values[k]), text);

// ฟอร์มที่แก้ได้ (สำเนาจากข้อมูลที่ backend ส่งมา)
const toForm = (s) => ({
  is_enabled: !!s.is_enabled,
  buckets: [...(s.buckets || [])],
  include_warranty: s.include_warranty !== false,
  include_filter: !!s.include_filter,
  include_pm: !!s.include_pm,
  pm_buckets: [...(s.pm_buckets || [])],
  cc_technician: !!s.cc_technician,
  frequency: s.frequency || "daily",
  weekly_day: s.weekly_day || 1,
  send_time: s.send_time || "08:00",
  urgent_immediate: !!s.urgent_immediate,
  include_owner_sales: !!s.include_owner_sales,
  attach_excel: !!s.attach_excel,
  recipients: (s.recipients || []).map((r) => ({
    type: r.type,
    kind: r.kind,
    user_uid: r.user_uid || null,
    name: r.name || "",
    email: r.email || "",
    is_active: r.is_active !== false,
  })),
});

// สถานะที่คำนวณจากฟอร์มปัจจุบัน (ให้ป้ายซ้ายเปลี่ยนทันทีที่แก้) - ตรงกับ toResponse ฝั่ง backend
const formStatus = (form) => {
  if (!form.is_enabled) return "off";
  return form.recipients.some((r) => r.type === "to" && r.is_active) ? "on" : "no_recipient";
};

// ลิงก์เก่า ?bu=BUxx -> แผนกแรกที่รหัสลงท้ายเลข BU (CBS002 = BU02)
const buSuffix = (bu) => {
  const m = /^BU(\d{2})$/i.exec(bu || "");
  return m ? `0${m[1]}` : "";
};

// หน้า "ตั้งค่าการแจ้งเตือน ประกัน/ไส้กรอง/PM" (ต่อแผนก) - field ตรงกับ SNAPA-Backend modules/admin/warranty-alert-setting
//   GET  admin/warranty-alert-settings                              -> array ต่อแผนก (department_uid, department_code, department_name)
//   PUT  admin/warranty-alert-settings/:department_uid              -> บันทึก (recipients: {type, user_uid | email})
//   POST admin/warranty-alert-settings/:department_uid/test-send    -> {sent_to,row_count,new_count}
//   GET  admin/warranty-alert-settings/:department_uid/preview-email?include_* -> {subject,html,row_count,new_count} (ไม่ส่ง ไม่ log)
export default class AdminWarrantyAlertSettings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      list: [],
      loaded: false,
      selected_uid: "",
      preview: null,
      form: null,
      base_json: "",
      users: [],
      input: { to: "", cc: "" },
      input_error: { to: "", cc: "" },
      focus_list: "",
      saving: false,
      search: "",
    };
  }

  async componentDidMount() {
    loading(false);
    const users = await GET("admin/users", { page_size: 10000, page: 0 });
    if (users?.status && Array.isArray(users.data)) this.setState({ users: users.data });
    await this.Load();
  }

  Load = async (keepUid = "") => {
    loading(true);
    const result = await GET("admin/warranty-alert-settings");
    loading(false);
    const list = result?.status && Array.isArray(result.data) ? result.data : [];
    this.setState({ list, loaded: true }, () => {
      const params = new URLSearchParams(window.location.search);
      const suffix = buSuffix(params.get("bu"));
      const wanted = keepUid || params.get("department_uid") || "";
      const target = list.find((d) => d.department_uid === wanted) || (suffix && list.find((d) => (d.department_code || "").endsWith(suffix))) || list[0];
      if (target) this.Select(target.department_uid, true);
      else this.setState({ selected_uid: "", form: null, base_json: "" });
    });
  };

  current = () => this.state.list.find((d) => d.department_uid === this.state.selected_uid);

  isDirty = () => !!this.state.form && JSON.stringify(this.state.form) !== this.state.base_json;

  Select = (uid, force = false) => {
    if (!force && uid === this.state.selected_uid) return;
    if (!force && this.isDirty() && !window.confirm(t("wx_set_unsaved"))) return;
    const item = this.state.list.find((d) => d.department_uid === uid);
    if (!item) return;
    const form = toForm(item);
    this.setState({ selected_uid: uid, form, base_json: JSON.stringify(form), input: { to: "", cc: "" }, input_error: { to: "", cc: "" }, focus_list: "" });
  };

  Discard = () => {
    const item = this.current();
    if (!item) return;
    const form = toForm(item);
    this.setState({ form, base_json: JSON.stringify(form), input: { to: "", cc: "" }, input_error: { to: "", cc: "" } });
  };

  SetForm = (patch) => this.setState({ form: { ...this.state.form, ...patch } });

  ToggleBucket = (value) => {
    const { buckets } = this.state.form;
    const next = buckets.includes(value) ? buckets.filter((b) => b !== value) : [...buckets, value];
    this.SetForm({ buckets: THRESHOLDS.filter((b) => next.includes(b)) });
  };

  TogglePmBucket = (value) => {
    const { pm_buckets } = this.state.form;
    const next = pm_buckets.includes(value) ? pm_buckets.filter((b) => b !== value) : [...pm_buckets, value];
    this.SetForm({ pm_buckets: PM_THRESHOLDS.filter((b) => next.includes(b)) });
  };

  SetTime = (part, value) => {
    const [h, m] = this.state.form.send_time.split(":");
    this.SetForm({ send_time: part === "h" ? `${value}:${m}` : `${h}:${value}` });
  };

  // ---------------------------------------------------------------- ผู้รับ
  SetInput = (list, value) => this.setState({ input: { ...this.state.input, [list]: value }, input_error: { ...this.state.input_error, [list]: "" }, focus_list: list });

  SetError = (list, message) => this.setState({ input_error: { ...this.state.input_error, [list]: message } });

  Suggestions = (list) => {
    const { users, input, form } = this.state;
    const keyword = input[list].trim().toLowerCase();
    if (!keyword || keyword.includes("@")) return [];
    const taken = new Set(form.recipients.filter((r) => r.type === list && r.user_uid).map((r) => r.user_uid));
    return users
      .filter((u) => u.is_active !== false && !taken.has(u.uid) && `${u.full_name || ""} ${u.email || ""}`.toLowerCase().includes(keyword))
      .slice(0, MAX_SUGGEST);
  };

  AddRecipient = (list, recipient) => {
    this.SetForm({ recipients: [...this.state.form.recipients, { type: list, is_active: true, user_uid: null, name: "", email: "", ...recipient }] });
    this.setState({ input: { ...this.state.input, [list]: "" }, input_error: { ...this.state.input_error, [list]: "" } });
  };

  AddUser = (list, user) => this.AddRecipient(list, { kind: "user", user_uid: user.uid, name: user.full_name || user.email || "", email: user.email || "" });

  OnRecipientKey = (list, e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const value = this.state.input[list].trim();
    if (!value) return;
    if (value.includes("@")) {
      if (!EMAIL_RE.test(value)) return this.SetError(list, t("wx_set_bad_email"));
      const exists = this.state.form.recipients.some((r) => r.type === list && r.kind === "email" && r.email.toLowerCase() === value.toLowerCase());
      if (exists) return this.SetError(list, t("wx_set_dup_recipient"));
      return this.AddRecipient(list, { kind: "email", email: value });
    }
    const [first] = this.Suggestions(list);
    if (!first) return this.SetError(list, t("wx_set_user_not_found"));
    return this.AddUser(list, first);
  };

  RemoveRecipient = (target) => this.SetForm({ recipients: this.state.form.recipients.filter((r) => r !== target) });

  // ---------------------------------------------------------------- บันทึก / ทดสอบ
  Validate = () => {
    const { form } = this.state;
    if (!form.is_enabled) return "";
    if (!form.include_warranty && !form.include_filter && !form.include_pm) return t("wx_set_need_type");
    if (!form.recipients.some((r) => r.type === "to")) return t("wx_set_need_to");
    if ((form.include_warranty || form.include_filter) && !form.buckets.length) return t("wx_set_need_bucket");
    if (form.include_pm && !form.pm_buckets.length) return t("wx_set_need_pm_bucket");
    return "";
  };

  Save = async () => {
    const problem = this.Validate();
    if (problem) {
      alert("", "warning", t("warning"), problem);
      return;
    }
    const { form, selected_uid } = this.state;
    this.setState({ saving: true });
    loading(true);
    const result = await PUT(`admin/warranty-alert-settings/${selected_uid}`, {
      is_enabled: form.is_enabled,
      buckets: form.buckets,
      include_warranty: form.include_warranty,
      include_filter: form.include_filter,
      include_pm: form.include_pm,
      pm_buckets: form.pm_buckets,
      cc_technician: form.include_pm ? form.cc_technician : false,
      frequency: form.frequency,
      weekly_day: form.frequency === "weekly" ? form.weekly_day : undefined,
      send_time: form.send_time,
      urgent_immediate: form.urgent_immediate,
      include_owner_sales: form.include_owner_sales,
      attach_excel: form.attach_excel,
      recipients: form.recipients.map((r) => (r.kind === "user" ? { type: r.type, user_uid: r.user_uid } : { type: r.type, email: r.email })),
    });
    loading(false);
    this.setState({ saving: false });
    if (!result?.status) return;
    alert("", "success", t("success"), t("wx_set_saved"));
    await this.Load(selected_uid);
  };

  // ดูตัวอย่างอีเมล (ไม่ส่ง ไม่บันทึก) - ประเภทที่ดูสลับได้ในโมดัล ค่าเริ่มต้นตามฟอร์มปัจจุบัน
  OpenPreview = (kinds) => {
    const { form } = this.state;
    const item = this.current();
    if (!form || !item) return;
    const start = kinds || { warranty: form.include_warranty, filter: form.include_filter, pm: form.include_pm };
    this.setState({ preview: { department_uid: item.department_uid, kinds: start, loading: true, data: null, error: false } }, this.FetchPreview);
  };

  FetchPreview = async () => {
    const { preview } = this.state;
    if (!preview) return;
    const seq = (this.previewSeq = (this.previewSeq || 0) + 1);
    const result = await GET(`admin/warranty-alert-settings/${preview.department_uid}/preview-email`, {
      include_warranty: !!preview.kinds.warranty,
      include_filter: !!preview.kinds.filter,
      include_pm: !!preview.kinds.pm,
    });
    if (seq !== this.previewSeq || !this.state.preview) return;
    const ok = !!result?.status && !!result.data;
    this.setState({ preview: { ...this.state.preview, loading: false, data: ok ? result.data : null, error: !ok } });
  };

  TogglePreviewKind = (k) => {
    const { preview } = this.state;
    const kinds = { ...preview.kinds, [k]: !preview.kinds[k] };
    if (!kinds.warranty && !kinds.filter && !kinds.pm) return;
    this.setState({ preview: { ...preview, kinds, loading: true } }, this.FetchPreview);
  };

  TestSend = async () => {
    loading(true);
    const result = await POST(`admin/warranty-alert-settings/${this.state.selected_uid}/test-send`, {});
    loading(false);
    if (!result?.status) return;
    const d = result.data || {};
    const kinds = Object.keys(d.kinds || {})
      .filter((k) => KIND_KEYS.includes(k))
      .map((k) => `${t(`wx_set_kind_${k}`)} ${d.kinds[k]}`)
      .join(" · ");
    alert("", "success", t("success"), fill(t("wx_set_test_sent"), { email: d.sent_to || "-", rows: d.row_count ?? 0, new: d.new_count ?? 0 }) + (kinds ? ` (${kinds})` : ""));
  };

  // ---------------------------------------------------------------- render
  renderBuList() {
    const { list, selected_uid, form, search } = this.state;
    const keyword = search.trim().toLowerCase();
    const shown = keyword ? list.filter((d) => `${d.department_code || ""} ${d.department_name || ""}`.toLowerCase().includes(keyword) || d.department_uid === selected_uid) : list;
    return (
      <div className="d-flex flex-column gap-2" data-testid="wx-dept-list">
        <input className="form-control" value={search} placeholder={t("was_search_ph")} onChange={(e) => this.setState({ search: e.target.value })} data-testid="wx-dept-search" />
        {shown.map((d) => {
          const selected = d.department_uid === selected_uid;
          const status = selected && form ? formStatus(form) : d.status;
          return (
            <button type="button" key={d.department_uid} className={`wx-bu-item btn ${selected ? "btn-primary" : "btn-white border"} px-3 py-2 rounded-3`} onClick={() => this.Select(d.department_uid)} data-testid={`wx-dept-${d.department_code}`}>
              <div>
                <b>{d.department_code}</b>
                <div className="text-12">{d.department_name}</div>
              </div>
              <span className="text-12 fw-bold ms-2 px-2 py-1 rounded-pill bg-white" style={{ color: STATUS_COLORS[status] }}>● {t(`wx_set_st_${status}`)}</span>
            </button>
          );
        })}
      </div>
    );
  }

  renderPreview() {
    const { preview, list } = this.state;
    if (!preview) return null;
    const item = list.find((d) => d.department_uid === preview.department_uid);
    const d = preview.data;
    return (
      <Modal show onHide={() => { this.previewSeq = (this.previewSeq || 0) + 1; this.setState({ preview: null }); }} centered size="xl" scrollable contentClassName="border-0 rounded-4 shadow-lg" data-testid="was-preview">
        <Modal.Header className="border-bottom border-light px-4 py-3 bg-white">
          <Modal.Title className="fw-bold text-dark text-18">{fill(t("was_pv_title"), { bu: `${item?.department_code || ""} – ${item?.department_name || ""}` })}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4" style={{ backgroundColor: "#F4F7FE" }}>
          <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
            <span className="text-12 fw-semibold text-secondary">{t("was_pv_types")}</span>
            {[["warranty", "wx_set_kind_warranty_full"], ["filter", "wx_set_kind_filter_full"], ["pm", "wx_set_kind_pm_full"]]
              .map(([k, label]) => (
                <label className="text-14 text-dark d-flex align-items-center gap-1" key={k}>
                  <input type="checkbox" checked={!!preview.kinds[k]} onChange={() => this.TogglePreviewKind(k)} data-testid={`was-pv-kind-${k}`} /> {t(label)}
                </label>
              ))}
          </div>
          <div className="text-secondary text-12 mb-3">{t("was_pv_note")}</div>
          {preview.loading ? <div className="text-center text-secondary py-5">{t("was_pv_loading")}</div> : null}
          {!preview.loading && preview.error ? <div className="alert alert-danger py-2 text-14">{t("was_pv_fail")}</div> : null}
          {!preview.loading && d ? (
            <>
              <div className="bg-white rounded-3 border p-3 mb-3 text-14" data-testid="was-pv-subject">
                <div><b>{t("was_pv_subject")}:</b> {d.subject}</div>
                <div className="text-secondary text-12 mt-1">{fill(t("was_pv_rows"), { rows: formatNumber(d.row_count ?? 0), new: formatNumber(d.new_count ?? 0) })}</div>
              </div>
              <iframe title="email-preview" sandbox="" srcDoc={d.html || ""} className="w-100 bg-white border rounded-3" style={{ height: "62vh" }} data-testid="was-pv-frame" />
            </>
          ) : null}
        </Modal.Body>
      </Modal>
    );
  }

  renderRecipients(list) {
    const { form, input, input_error, focus_list } = this.state;
    const chips = form.recipients.filter((r) => r.type === list);
    const suggestions = focus_list === list ? this.Suggestions(list) : [];
    return (
      <div className="mb-2">
        <label className="form-label text-12 text-secondary mb-1 w-100">
          {t(`wx_set_${list}`)}
          <div className={`wx-recip ${input_error[list] ? "bad" : ""}`} data-testid={`wx-recip-${list}`}>
            {chips.map((r, i) => (
              <span key={`${r.kind}-${r.user_uid || r.email}-${i}`} className={`r ${r.kind === "user" ? "user" : ""} ${r.is_active ? "" : "inactive"}`} title={r.is_active ? "" : t("wx_set_inactive_tip")}>
                {r.kind === "user" ? "👤 " : "✉️ "}
                {r.kind === "user" ? r.name || r.email : r.email}
                {r.is_active ? null : <span className="text-12 fw-semibold text-secondary bg-light px-2 py-1 rounded-pill" style={{ textDecoration: "none" }}>{t("wx_set_inactive")}</span>}
                <button type="button" aria-label="remove" onClick={() => this.RemoveRecipient(r)}>×</button>
              </span>
            ))}
            <input
              value={input[list]}
              placeholder={t("wx_set_recipient_ph")}
              onChange={(e) => this.SetInput(list, e.target.value)}
              onKeyDown={(e) => this.OnRecipientKey(list, e)}
              onFocus={() => this.setState({ focus_list: list })}
              onBlur={() => this.setState({ focus_list: "" })}
            />
            {suggestions.length ? (
              <div className="wx-suggest">
                {suggestions.map((u) => (
                  <button type="button" key={u.uid} onMouseDown={(e) => { e.preventDefault(); this.AddUser(list, u); }}>
                    👤 {u.full_name} <span className="text-secondary text-12">{u.email}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </label>
        {input_error[list] ? <div className="text-danger text-12" data-testid={`wx-recip-error-${list}`}>{input_error[list]}</div> : null}
      </div>
    );
  }

  renderSwitch(checked, onChange, label, testid) {
    return (
      <div className="form-check form-switch mb-2">
        <label className="form-check-label text-14 text-dark">
          <input className="form-check-input" type="checkbox" role="switch" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
          {label}
        </label>
      </div>
    );
  }

  renderForm() {
    const { form, saving } = this.state;
    const item = this.current();
    if (!form || !item) return null;
    const dirty = this.isDirty();
    const status = formStatus(form);
    const [hour, minute] = form.send_time.split(":");
    const minutes = MINUTES.includes(minute) ? MINUTES : [...MINUTES, minute].sort();
    const disabledStyle = form.is_enabled ? undefined : { opacity: 0.45, pointerEvents: "none" };

    return (
      <div className="bg-white rounded-4 shadow-sm p-4" data-testid="wx-form">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
          <div>
            <h5 className="fw-bold mb-0 text-dark">{item.department_code} – {item.department_name}</h5>
            <div className="text-secondary text-12">
              {item.configured && item.updated_at
                ? fill(t("wx_set_updated"), { at: fmtDateTime(item.updated_at), by: item.updated_by_name || "-" })
                : t("wx_set_never")}
            </div>
          </div>
          {this.renderSwitch(form.is_enabled, (v) => this.SetForm({ is_enabled: v }), t("wx_set_enable"), "wx-enable")}
        </div>

        {form.is_enabled && status === "no_recipient" ? <div className="alert alert-danger py-2 text-14 fw-semibold" data-testid="wx-no-recipient">{t("wx_set_no_to_warn")}</div> : null}
        {(item.warnings || []).map((w, i) => <div className="alert alert-warning py-2 text-14 fw-semibold" key={`${w.code}-${i}`}>{w.message}</div>)}

        <div style={disabledStyle}>
          <div className="text-14 fw-bold text-dark mb-2">{t("was_s_types")}</div>
          <div className="mb-1 d-flex flex-wrap column-gap-4" data-testid="wx-kinds">
            {this.renderSwitch(form.include_warranty, (v) => this.SetForm({ include_warranty: v }), t("wx_set_kind_warranty_full"), "wx-kind-warranty")}
            {this.renderSwitch(form.include_filter, (v) => this.SetForm({ include_filter: v }), t("wx_set_kind_filter_full"), "wx-kind-filter")}
            {this.renderSwitch(form.include_pm, (v) => this.SetForm({ include_pm: v }), t("wx_set_kind_pm_full"), "wx-kind-pm")}
          </div>
          <div className="text-secondary text-12 mb-2">{t("wx_set_kinds_hint")}</div>
          {!form.include_warranty && !form.include_filter && !form.include_pm ? <div className="alert alert-warning py-2 text-14 fw-semibold" data-testid="wx-no-kind">{t("wx_set_need_type")}</div> : null}

          <div className="text-14 fw-bold text-dark mb-2 mt-3">{t("wx_set_s1")}</div>
          <div className="row g-2 align-items-center mb-4" data-testid="wx-thresholds">
            {form.include_warranty || form.include_filter ? (
              <>
                <div className="col-md-3 text-12 fw-semibold text-secondary">{t("wx_set_thr_warranty_filter")}</div>
                <div className="col-md-9 d-flex flex-wrap gap-2">
                  {THRESHOLDS.map((b) => (
                    <button type="button" key={b} className={`btn btn-sm ${form.buckets.includes(b) ? "btn-primary" : "btn-white border"} rounded-3 fw-semibold`} onClick={() => this.ToggleBucket(b)} data-testid={`wx-thr-${b}`}>
                      {fill(t("wx_set_days"), { n: b })}
                    </button>
                  ))}
                </div>
              </>
            ) : null}
            {form.include_pm ? (
              <>
                <div className="col-md-3 text-12 fw-semibold text-secondary">{t("wx_set_thr_pm")}</div>
                <div className="col-md-9 d-flex flex-wrap gap-2 align-items-center">
                  {PM_THRESHOLDS.map((b) => (
                    <button type="button" key={b} className={`btn btn-sm ${form.pm_buckets.includes(b) ? "btn-primary" : "btn-white border"} rounded-3 fw-semibold`} onClick={() => this.TogglePmBucket(b)} data-testid={`wx-pm-thr-${b}`}>
                      {fill(t("wx_set_days"), { n: b })}
                    </button>
                  ))}
                  <span className="text-12 fw-semibold px-2 py-1 rounded-3 wx-t-ovd" title={t("wx_set_pm_overdue_tip")} data-testid="wx-pm-overdue">⛔ {t("wx_set_pm_overdue")}</span>
                </div>
              </>
            ) : null}
            {!form.include_warranty && !form.include_filter && !form.include_pm ? <div className="col-12 text-secondary text-12">{t("wx_set_thr_pick_type")}</div> : null}
          </div>

          <div className="text-14 fw-bold text-dark mb-2">{t("wx_set_s2")}</div>
          {this.renderRecipients("to")}
          {this.renderRecipients("cc")}
          <div className="text-secondary text-12 mb-2">{t("wx_set_recipient_hint")}</div>
          {this.renderSwitch(form.include_owner_sales, (v) => this.SetForm({ include_owner_sales: v }), t("wx_set_sales"))}
          <div className="mb-4">
            {form.include_pm ? this.renderSwitch(form.cc_technician, (v) => this.SetForm({ cc_technician: v }), t("wx_set_cc_technician"), "wx-cc-technician") : null}
          </div>

          <div className="text-14 fw-bold text-dark mb-2">{t("wx_set_s3")}</div>
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <label className="form-label text-12 text-secondary mb-1">{t("wx_set_freq")}</label>
              <select className="form-select" value={form.frequency} onChange={(e) => this.SetForm({ frequency: e.target.value })}>
                <option value="daily">{t("wx_set_freq_daily")}</option>
                <option value="weekly">{t("wx_set_freq_weekly")}</option>
              </select>
            </div>
            {form.frequency === "weekly" ? (
              <div className="col-md-4">
                <label className="form-label text-12 text-secondary mb-1">{t("wx_set_day")}</label>
                <select className="form-select" value={form.weekly_day} onChange={(e) => this.SetForm({ weekly_day: Number(e.target.value) })}>
                  {WEEK_DAYS.map((d) => <option key={d} value={d}>{t(`wx_set_day_${d}`)}</option>)}
                </select>
              </div>
            ) : null}
            <div className="col-md-4">
              <label className="form-label text-12 text-secondary mb-1">{t("wx_set_time")}</label>
              <div className="d-flex align-items-center gap-1">
                <select className="form-select" value={hour} onChange={(e) => this.SetTime("h", e.target.value)} aria-label="hour">
                  {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
                :
                <select className="form-select" value={minute} onChange={(e) => this.SetTime("m", e.target.value)} aria-label="minute">
                  {minutes.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
          </div>
          {this.renderSwitch(form.urgent_immediate, (v) => this.SetForm({ urgent_immediate: v }), t("wx_set_urgent"))}
          {this.renderSwitch(form.attach_excel, (v) => this.SetForm({ attach_excel: v }), t("wx_set_excel"))}
        </div>

        <div className="d-flex justify-content-between flex-wrap gap-2 pt-3 mt-3 border-top border-light">
          <div className="d-flex gap-2 flex-wrap">
          <button type="button" className="btn btn-white border px-3 py-2 fw-bold rounded-3" onClick={() => this.OpenPreview()} data-testid="was-preview-btn">
            👁 {t("was_preview")}
          </button>
          <button type="button" className="btn btn-white border px-3 py-2 fw-bold rounded-3" onClick={this.TestSend} disabled={dirty || !item.configured} title={dirty || !item.configured ? t("wx_set_test_hint") : ""} data-testid="wx-test-send">
            ✉️ {t("wx_set_test")}
          </button>
          </div>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-white border px-3 py-2 fw-bold rounded-3" onClick={this.Discard} disabled={!dirty}>{t("wx_set_reset")}</button>
            <button type="button" className="btn btn-primary px-4 py-2 fw-bold rounded-3" onClick={this.Save} disabled={saving || (!dirty && item.configured)} data-testid="wx-save">{t("save")}</button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { list, loaded } = this.state;
    return (
      <div className="w-100 min-vh-100" style={{ backgroundColor: "#F4F7FE" }}>
        <NavbarAdmin />
        <div className="body p-4 mt-3">
          <div className="mb-4">
            <h3 className="fw-bold mb-1 text-dark">{t("was_title")}</h3>
            <div className="text-secondary text-14">{t("was_desc")}</div>
          </div>
          {loaded && !list.length ? <div className="bg-white rounded-4 shadow-sm p-5 text-center text-secondary">{t("was_no_dept")}</div> : null}
          {list.length ? (
            <div className="row g-3">
              <div className="col-lg-3">
                <div className="bg-white rounded-4 shadow-sm p-3">{this.renderBuList()}</div>
              </div>
              <div className="col-lg-9">{this.renderForm()}</div>
            </div>
          ) : null}
        </div>
        {this.renderPreview()}
      </div>
    );
  }
}
